import { type Dest } from '../types/dest'

export type ApiCategory = {
  id: number
  slug: string
  name: string
}

export type ApiPhoto = {
  url: string
  alt: string | null
  sort: number
}

export type ApiBadge = {
  slug: string
  name: string
}

export type ApiProduct = {
  id: number
  slug: string
  name: string
  summary?: string | null
  description: string | null
  price: number
  currency: string
  photos: ApiPhoto[]
  categories: ApiCategory[]
  badges?: ApiBadge[]
}

// legacy PaymentIntent/order types removed

export type ApiCheckoutSessionRequest = {
  items: Array<{
    product_slug: string
    qty: number
  }>
  buyer_email?: string
}

export type ApiCheckoutSessionResponse = {
  data: {
    id: string
    url: string
  }
}

export type ApiCheckoutSuccessItem = {
  item_id: string
  item_name: string
  price: number
  quantity: number
}

export type ApiCheckoutSuccessResponse = {
  data: {
    order_id: number
    status: string
    payment_intent_id: string
    currency: string
    amounts: {
      subtotal: number
      shipping: number
      tax: number
      discount: number
      total: number
    }
    items: ApiCheckoutSuccessItem[]
  }
}

type ApiOk<T> = { data: T }

type ApiError = {
  error: {
    code: string
    message: string
    details?: unknown
  }
}

export type ApiLang = 'pt' | 'en' | 'fr' | 'es'

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init?.headers || {}),
      'Accept': 'application/json',
    },
  })

  const text = await res.text()
  const parsed = text ? (JSON.parse(text) as unknown) : null

  if (!res.ok) {
    const err = parsed as ApiError | null
    const code = err?.error?.code || 'request_failed'
    // Use a stable, localizable message (the code). UI should map `code` → translations.
    // Keep the server-provided message separately for debugging.
    const e = new Error(code)
    ;(e as any).code = code
    ;(e as any).status = res.status
    ;(e as any).details = err?.error?.details
    ;(e as any).serverMessage = err?.error?.message
    throw e
  }

  return parsed as T
}

export async function listProducts(opts?: { category?: string; lang?: ApiLang; destination?: Dest }): Promise<ApiProduct[]> {
  const qp: string[] = []
  if (opts?.category) qp.push(`category=${encodeURIComponent(opts.category)}`)
  if (opts?.lang) qp.push(`lang=${encodeURIComponent(opts.lang)}`)
  if (opts?.destination) qp.push(`destination=${encodeURIComponent(opts.destination)}`)
  const qs = qp.length ? `?${qp.join('&')}` : ''
  const res = await fetchJson<ApiOk<ApiProduct[]>>(`/api/products${qs}`)
  return res.data
}

export async function getProduct(slugOrId: string, opts?: { lang?: ApiLang }): Promise<ApiProduct> {
  const qs = opts?.lang ? `?lang=${encodeURIComponent(opts.lang)}` : ''
  const res = await fetchJson<ApiOk<ApiProduct>>(`/api/products/${encodeURIComponent(slugOrId)}${qs}`)
  return res.data
}

// legacy PaymentIntent/order endpoints removed

export async function createCheckoutSession(
  payload: ApiCheckoutSessionRequest,
  opts?: { lang?: ApiLang }
): Promise<ApiCheckoutSessionResponse['data']> {
  const qs = opts?.lang ? `?lang=${encodeURIComponent(opts.lang)}` : ''
  const res = await fetchJson<ApiCheckoutSessionResponse>(`/api/checkout-session${qs}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return res.data
}

export async function completeCheckoutSession(
  sessionId: string,
  opts?: { lang?: ApiLang }
): Promise<ApiCheckoutSuccessResponse['data']> {
  const qp: string[] = [`session_id=${encodeURIComponent(sessionId)}`]
  if (opts?.lang) qp.push(`lang=${encodeURIComponent(opts.lang)}`)
  const qs = `?${qp.join('&')}`
  const res = await fetchJson<ApiCheckoutSuccessResponse>(`/api/checkout-success${qs}`)
  return res.data
}
